import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { LayoutService } from './../../../shared/services/layout.service';
import { TermsConditionTopicComponent } from './terms-condition-topic/terms-condition-topic.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [MatExpansionModule, MatIconModule, CommonModule, TermsConditionTopicComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  authService = inject(AuthService);
  isMobile = inject(LayoutService).isMobile;
  router = inject(Router);

  faqs = [
    {
      question: 'What is the Medical Assistance Transportation Program (MATP)?',
      answer:
        'MATP helps Medical Assistance (MA) recipients get to and from covered medical appointments. MATP pays for the lowest-cost option that fits your needs. In Pennsylvania, MATP is run at the county level. Your local county MATP office handles your enrollment, scheduling, and service. The PA Department of Human Services (DHS) oversees the program.',
    },
    {
      question: 'Who is eligible for MATP?',
      answer:
        'You may qualify if you are enrolled in Medical Assistance and have a scheduled appointment for a covered MA service. If you have a car or someone who can drive you, mileage reimbursement may be your best option. If you live within a quarter mile of a public transit stop, MATP may pay your fare. This includes the bus, train, trolley, or subway. If neither of those works for you, MATP may arrange paratransit.',
    },
    {
      question: 'How do I enroll in MATP?',
      answer:
        'To get started, go to matp.pa.gov and search for your county to find your local MATP office. Staff there will help you through eligibility and enrollment. You can also apply for MATP—and other transportation options like Senior Shared Ride or Persons with Disabilities (PWD)—at www.apply.findmyride.penndot.pa.gov. ',
    },
    {
      question: 'How does mileage reimbursement work?',
      answer:
        'If you drive your own car to a covered medical appointment, MATP may reimburse you for the miles driven, plus parking and tolls. The same applies if someone you know drives you. You submit your trip details through this system. If approved, a reimbursement check is sent to you or your personal representative. You can also request mileage reimbursement using a paper form, which your local county MATP office can provide.',
    },
    {
      question: 'How much will I be reimbursed?',
      answer:
        'The current reimbursement rate is $0.25 per mile. Mileage is based on the most direct route from your starting point to your appointment and back. If you had to take a different way, let your county MATP agency know. If you have more than one covered appointment or need to stop at the pharmacy to pick up a prescription on the way home, you may be reimbursed for those miles too. MATP will also pay you back for parking or tolls you paid as part of driving to your appointment when you have a receipt.',
    },
    {
      question: 'How long do I have to submit a reimbursement request?',
      answer:
        'You must submit your complete reimbursement request within 60 days of your trip. Requests sent after 60 days may not be accepted. You can submit online through this system, by fax, by mail (the postmark date counts), or in person at your county office.',
    },
    {
      question: 'How long does it take to receive my reimbursement?',
      answer:
        'After your request is reviewed and approved, your county must send payment within 20 business days. If they need more paperwork from you, payment must be sent by the last day of the following month. If your reimbursement is less than $10, your county MATP may hold it until your total reaches $10 or the next payment cycle. Contact your county office to ask about their specific payment schedule.',
    },
    {
      question: 'What documentation might I need to provide?',
      answer:
        'Your county MATP agency will check several things. They will confirm your MA and MATP eligibility. They will verify that the medical provider you visited is eligible and that the service was covered by MA. They will also confirm the distance of your trip. Your county may ask for extra documents, but they cannot require you to use any form other than the standard DHS-approved MATP Mileage Reimbursement Form. If you paid for parking or tolls, make sure you have your receipt.',
    },
    {
      question: 'What is pre-trip notification, and do I need it?',
      answer:
        'Pre-trip notification means calling your county MATP office before your trip. It is not always required. But your county must require it in three cases. The first is during your first three months using mileage reimbursement through MATP. The second is if your trip goes outside your county’s service area. The third is if there are concerns about past submissions. If you’re not sure, call your county before you go.',
    },
    {
      question: 'My appointment is outside my county or normal service area. Am I still covered?',
      answer:
        'Possibly, but you must call your county MATP office before your trip. You need to confirm coverage and get approval first. In most cases, you will need to show medical necessity if you are traveling far when a closer Medicaid in-network provider is available. Do not assume a trip outside your local service area will be reimbursed. If you submit without prior approval, your request may be denied.',
    },
    {
      question: 'I drove another MA consumer to their appointment in the same trip. How do I handle that?',
      answer:
        'Only one reimbursement can be submitted for shared mileage. You cannot each submit a request for the same miles driven. Contact your county office for help on how to submit correctly in this situation.',
    },
    {
      question: 'I carpooled with another MA consumer to a nearby appointment. Can I still be reimbursed?',
      answer:
        'Only one eligible MA recipient can be reimbursed per vehicle. It does not matter how many people are riding. If more than one MA recipient submits a reimbursement request for the same carpool trip, this may be reviewed for fraud, waste, and abuse.',
    },
    {
      question: 'The appointment was for my child. Who will the reimbursement check be made out to?',
      answer:
        'Reimbursement is issued to the consumer or their representative. If the consumer is a minor, the check is usually paid to the parent, legal guardian, or authorized representative on file with your county. Not sure whose name is on file? Contact your county MATP office before you submit.',
    },
    {
      question: 'What if I submitted an incomplete form?',
      answer:
        'Your county will try to reach you at least two times to get the missing information. If they cannot reach you, they will send you a letter and give you 15 days to respond. If your form has some trips with complete information and some without, your county must pay for the complete ones. They can only deny the trips where information is still missing.',
    },
    {
      question: 'Who do I contact if I have questions?',
      answer:
        'Contact your county MATP office. They run the program locally and can answer questions about your eligibility, enrollment, trips, or reimbursement requests. For questions about your COMPASS account, call the COMPASS Helpdesk at 800-692-7462. For questions about your Medical Assistance benefits or eligibility, call the Office of Income Maintenance Customer Service Center at 877-395-8930 or 215-560-7226 in Philadelphia County. If you need to apply for benefits, you can call your local County Assistance Office or 866-550-4355 to reach Consumer Services.',
    },
  ];

  navigateToContactUs() {
    this.router.navigate(['contact-us']);
  }

  logout() {
    this.authService.logout();
  }
}
